export type ApiErrorCode =
  | "bad_request"
  | "unauthorized"
  | "forbidden"
  | "not_found"
  | "conflict"
  | "rate_limited"
  | "expired"
  | "consumed"
  | "quota"
  | "service_unavailable";

export interface ApiError {
  code: ApiErrorCode;
  message: string;
}

export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: ApiErrorCode;
  constructor(status: number, code: ApiErrorCode, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`/api${path}`, {
    ...init,
    credentials: "same-origin",
    headers: {
      ...(init.body ? { "Content-Type": "application/json" } : {}),
      ...init.headers,
    },
  });
  if (!res.ok) {
    let err: ApiError = { code: "service_unavailable", message: "Something went wrong." };
    try {
      err = (await res.json()) as ApiError;
    } catch {
      // fall back to generic
    }
    throw new ApiRequestError(res.status, err.code, err.message);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  requestMagicLink: (email: string, inviteCode?: string) =>
    request<{ accepted: boolean }>("/auth/request", {
      method: "POST",
      body: JSON.stringify({ email, ...(inviteCode ? { inviteCode } : {}) }),
    }),
  redeem: (token: string, operationId: string) =>
    request<{ user: unknown; group: unknown }>("/auth/redeem", {
      method: "POST",
      body: JSON.stringify({ token, operationId }),
    }),
  logout: () => request<void>("/auth/logout", { method: "POST" }),
  logoutAll: () => request<void>("/auth/logout-all", { method: "POST" }),
  me: () => request<import("../../shared/api.js").MeResponse>("/me"),
  updateMe: (patch: Record<string, unknown>) =>
    request<{ updated: boolean }>("/me", {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),
  createInvite: (groupId: string, body: Record<string, unknown>) =>
    request<import("../../shared/api.js").CreateInviteResponse>(`/groups/${groupId}/invites`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  revokeInvite: (groupId: string, inviteId: string) =>
    request<void>(`/groups/${groupId}/invites/${inviteId}`, { method: "DELETE" }),
};
